import type { SerializerOptions } from '@ember-data/legacy-compat';
import type { Snapshot } from '@ember-data/legacy-compat/-private';
import type { ModelSchema } from '@ember-data/store/types';

import type { RecordAttributes, RecordData } from 'webapp-types';
import { omit } from 'webapp/utils/common';

import ApplicationSerializer from 'webapp/serializers/application';

import { DEFAULT_BG_IMAGE_OPACITY } from 'webapp/models/board';
import type ReportSlideRecord from 'webapp/models/report-slide';

export default class ReportSlideSerializer extends ApplicationSerializer {
	// json is Raw when it comes from BE, Serialized when BufferedModel.toJSON() runs a record through normalize
	override normalize(
		typeClass: ModelSchema,
		json: RecordData.ReportSlide.Raw | RecordData.ReportSlide.Serialized,
		prop: string | undefined,
	) {
		const { settings, ...data } = json;
		const attributes: Partial<RecordAttributes<ReportSlideRecord>> & { id?: string | number } =
			{
				...data,
			};

		if (settings) {
			// undefined values are skipped by ED
			attributes.color = settings.color;
			attributes.themeId = settings.themeId;
			attributes.logo = settings.logo;
			attributes.logoOriginal = settings.logoOriginal;
			attributes.logoCrop = settings.logoCrop;
			attributes.bg = settings.bg;
			attributes.bgOriginal = settings.bgOriginal;
			attributes.bgCrop = settings.bgCrop;
			attributes.bgOpacity = settings.bgOpacity;
		}

		if (!attributes.bgOpacity) {
			attributes.bgOpacity = DEFAULT_BG_IMAGE_OPACITY;
		}

		return super.normalize(typeClass, attributes, prop);
	}

	override serialize(
		snapshot: Snapshot,
		options: SerializerOptions,
	): RecordData.ReportSlide.Serialized {
		const attributes = super.serialize(
			snapshot,
			options,
		) as RecordAttributes<ReportSlideRecord>;

		const settings: RecordData.ReportSlide.Settings = {};
		if (attributes.color) {
			settings.color = attributes.color;
		}
		if (attributes.themeId) {
			settings.themeId = attributes.themeId;
		}
		if (attributes.logo) {
			settings.logo = attributes.logo;
			if (attributes.logoOriginal) {
				settings.logoOriginal = attributes.logoOriginal;
			}
			if (attributes.logoCrop) {
				settings.logoCrop = attributes.logoCrop;
			}
		}
		if (attributes.bg) {
			settings.bg = attributes.bg;
			if (attributes.bgOriginal) {
				settings.bgOriginal = attributes.bgOriginal;
			}
			if (attributes.bgCrop) {
				settings.bgCrop = attributes.bgCrop;
			}
			if (attributes.bgOpacity) {
				settings.bgOpacity = attributes.bgOpacity;
			}
		}

		// BE only reads these from settings (and replaces the settings with the sent ones)
		return {
			...omit(
				attributes,
				'color',
				'themeId',
				'logo',
				'logoOriginal',
				'logoCrop',
				'bg',
				'bgOriginal',
				'bgCrop',
				'bgOpacity',
			),
			settings,
		};
	}
}
